import { IComponentOptions } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { GtmhubController } from "@gtmhub/core";
import { ContributorType, IGoal, IGoalChildReorderResponse, OkrMethodologySettings } from "@gtmhub/goals/models";
import { localize } from "@gtmhub/localization";
import { ItemType } from "@gtmhub/okrs/alignment-tree/models";
import { formatAttainmentProgressValue, isContributingMetric } from "@gtmhub/okrs/metrics/utils";
import { DndListItemComponentCtrl } from "@gtmhub/shared/components/dnd-list-item/dnd-list-item.component";
import { DndListComponentCtrl } from "@gtmhub/shared/components/dnd-list/dnd-list.component";
import { INgRedux } from "@gtmhub/state-management";
import { isUnknownAssignee } from "@webapp/assignees/utils/assignee.utils";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { IMetricContributorActionHandlers, IMetricContributorContext } from "@webapp/okrs/metrics/models/metric-contributor-dropdown.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { getDisplayableOwnerIds, getProgressDescription } from "@webapp/okrs/utils/utils";
import { Session } from "@webapp/sessions/models/sessions.model";

interface IContributorComponentBindings {
  goal: IGoal;
  planningSession: Session;
  allowedActions: string[];
  isTrialAccount: boolean;
  isAllowedToSubmitToMarketplace: boolean;
  methodologySettings: OkrMethodologySettings;
  addChildObjective(itemId: string, itemType: ItemType, itemSessionId: string): void;
  openMoveMetricModal(metric: Metric): void;
  openUpdateMetricModal(metricId: string): void;
  openCloneMetricModal(metric: Metric): void;
  openDisableSyncModal(metricId: string): void;
  confirmDeleteMetric(metricId: string): void;
  canUpdateMetric(metric: Metric): boolean;
  handleOpenCascadingForm(metric: Metric, goalName: string): void;
  copyItemUrlToClipboard(itemId: string, type: ItemType): void;
}

const bindStateToCtrl = (state: IAssigneesStoreState) => {
  return {
    assigneeIdMap: state.assignees.map,
  };
};

interface ContributorComponentCtrl extends IContributorComponentBindings, ReturnType<typeof bindStateToCtrl> {
  ghDndList: DndListComponentCtrl<IGoalChildReorderResponse, ContributorType>;
  ghDndListItem: DndListItemComponentCtrl<ContributorType>;
  type: "goal" | "metric";
  contributor: ContributorType;
  metric?: Metric;
  isContributingMetric: boolean;
  metricContributorActionHandlers: IMetricContributorActionHandlers;
  metricContributorContext: IMetricContributorContext;
  formatAttainmentProgressValue(progress: number): number;
}

class ContributorComponentCtrl extends GtmhubController {
  static $inject = ["ProgressColoring", "$ngRedux", "$state", "UpdateMetricModalService"];

  public ownerIds: string[];

  constructor(private progressColoring: ProgressColoring, private $ngRedux: INgRedux, private state: StateService) {
    super();
    this.formatAttainmentProgressValue = formatAttainmentProgressValue;
  }

  $onInit(): void {
    this.onDestroy(this.$ngRedux.connect(bindStateToCtrl)(this));
    this.contributor = this.ghDndListItem.item;
    this.type = this.contributor.childType;

    if (this.ghDndListItem.item.childType === "metric") {
      this.metric = this.ghDndListItem.item;
      this.isContributingMetric = isContributingMetric(this.metric);
    }

    if (this.type === "metric") {
      this.setMetricContributorActionHandlersAndContext();
    }

    this.ownerIds = getDisplayableOwnerIds(this.contributor, this.type, this.methodologySettings);
  }

  onKeyResultKeyPress(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      this.state.go("._metric.metric", { metricId: this.contributor.id });
    }
  }

  get metricAriaLabelDescription(): string {
    if (this.type !== "metric") {
      return "";
    }

    const metricTargetDescription = document.getElementById(`metric-target-container-${this.contributor.id}`)?.textContent;
    let confidenceDescription = "";
    if ((this.contributor as Metric).confidence) {
      const confidenceLabel = document.getElementById(`confidence-preview-${this.contributor.id}`)?.textContent;
      confidenceDescription = `${localize("confidence_level")}: ${confidenceLabel} `;
    }

    const colorSetup = this.progressColoring.getColoring();
    const progressDescription = getProgressDescription(colorSetup, this.contributor.attainment, this.contributor.color);

    return `${this.contributor.name} ${metricTargetDescription} ${confidenceDescription} ${this.metricAssigneeDescription} ${progressDescription} `;
  }

  get metricAssigneeDescription(): string {
    let assigneesDescription = "";
    if (this.contributor?.ownerIds === undefined) {
      return assigneesDescription;
    }

    for (const assigneeId of this.contributor.ownerIds) {
      const assignee = this.assigneeIdMap[assigneeId];
      const isDeactivated = assignee && !isUnknownAssignee(assignee) && assignee.isActive === false;
      const isUnknown = !assignee || isUnknownAssignee(assignee);
      let assigneeDescription = "";

      if (!isUnknown && !isDeactivated) {
        assigneeDescription = assignee.name + " ";
      } else if (isDeactivated && !isUnknown) {
        assigneeDescription = `${assignee.name} ${localize("deactivated")}`;
      } else {
        assigneeDescription = localize("deleted_user");
      }

      assigneesDescription = assigneesDescription + assigneeDescription + " ";
    }

    return assigneesDescription;
  }

  private setMetricContributorActionHandlersAndContext(): void {
    this.metricContributorActionHandlers = {
      move: this.openMoveMetricModal,
      update: this.openUpdateMetricModal,
      cascade: this.handleOpenCascadingForm,
      clone: this.openCloneMetricModal,
      disableSync: this.openDisableSyncModal,
      copyUrl: this.copyItemUrlToClipboard,
      delete: this.confirmDeleteMetric,
    };

    this.metricContributorContext = {
      metric: this.metric,
      goal: this.goal as Goal,
      session: this.planningSession,
      allowedActions: this.allowedActions,
    };
  }
}

export const ContributorComponent: IComponentOptions = {
  template: require("./contributor.component.html"),
  controller: ContributorComponentCtrl,
  require: { ghDndListItem: "^", ghDndList: "^" },
  bindings: {
    goal: "<",
    planningSession: "<",
    allowedActions: "<",
    isTrialAccount: "<",
    isAllowedToSubmitToMarketplace: "<",
    methodologySettings: "<",
    addChildObjective: "<",
    openMoveMetricModal: "<",
    openUpdateMetricModal: "<",
    openCloneMetricModal: "<",
    openDisableSyncModal: "<",
    confirmDeleteMetric: "<",
    canUpdateMetric: "<",
    handleOpenCascadingForm: "<",
    copyItemUrlToClipboard: "<",
  },
};
